import { Download, Clock, User } from 'lucide-react';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Skeleton } from '@/app/components/ui/skeleton';
import { useDocumentVersions } from '@/hooks/use-setup-wizard';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { GeneratedDocumentVersion } from '@/lib/types';

export function DocumentVersionTimeline({ docId }: { docId: number }) {
  const { data: versions, isLoading } = useDocumentVersions(docId);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-700">Version History</h3>
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className="h-16" />
        ))}
      </div>
    );
  }

  const list = versions || [];
  if (list.length === 0) {
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-700">Version History</h3>
        <p className="text-sm text-gray-500">No published versions yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-700">Version History</h3>
      <div className="relative space-y-0">
        {list.map((v, idx) => (
          <VersionEntry key={v.id} version={v} isLast={idx === list.length - 1} />
        ))}
      </div>
    </div>
  );
}

function VersionEntry({
  version,
  isLast,
}: {
  version: GeneratedDocumentVersion;
  isLast: boolean;
}) {
  const timeZone = useOrgTimeZone();
  return (
    <div className="relative flex gap-3 pb-4">
      {/* Timeline line */}
      {!isLast && (
        <div className="absolute left-[9px] top-5 h-full w-px bg-gray-200" />
      )}
      {/* Dot */}
      <div className="relative z-10 mt-1.5 h-[18px] w-[18px] shrink-0 rounded-full border-2 border-blue-500 bg-white" />
      {/* Content */}
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="font-mono text-xs">
            v{version.version}
          </Badge>
          <span className="flex items-center gap-1 text-xs text-gray-500">
            <Clock className="h-3 w-3" />
            {version.published_at
              ? formatDateTimeInTimeZone(version.published_at, timeZone)
              : ''}
          </span>
        </div>
        {version.published_by_display && (
          <p className="flex items-center gap-1 text-xs text-gray-500">
            <User className="h-3 w-3" />
            {version.published_by_display}
          </p>
        )}
        {version.change_summary && (
          <p className="text-sm text-gray-700">{version.change_summary}</p>
        )}
        {version.download_url && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            asChild
          >
            <a
              href={version.download_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Download className="h-3 w-3" />
              Download PDF
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}
